import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@utils/consts';
import { useAccess } from '@hooks/access';

// TODO: Eliminate throttle as it may not be needed
// Left commented until we approve the fix

// const REFETCH_THROTTLE_MS = 1000;
// const lastRefetchedAt = new Map<string, number>();

// const PAUSE_REVALIDATION_TTL_MS = 30000;
const pausedRevalidationKeys = new Map<string, number>();

// Skips websocket-pushed revalidation for a query while a client-side action is
// awaiting user confirmation (e.g. an optimistically-moved schedule pending an
// overlap/specialty dialog decision), so an unrelated push notification can't
// clobber that pending state before the user resolves it. Self-expires so a
// missed resumeRevalidation call (an unhandled dialog/early-return path) can't
// permanently silence live updates for that query.
export const pauseRevalidation = (queryKey: string[]) => {
  pausedRevalidationKeys.set(queryKey.join('/'), Date.now());
};

export const resumeRevalidation = (queryKey: string[]) => {
  pausedRevalidationKeys.delete(queryKey.join('/'));
};

// const isRevalidationPaused = (key: string) => {
//   const pausedAt = pausedRevalidationKeys.get(key);
//   if (pausedAt === undefined) return false;
//   if (Date.now() - pausedAt > PAUSE_REVALIDATION_TTL_MS) {
//     pausedRevalidationKeys.delete(key);
//     return false;
//   }
//   return true;
// };

export const useRevalidateQuery = () => {
  const queryClient = useQueryClient();
  const access = useAccess();

  const revalidateQuery = async (qKeys: string[]) => {
    // const key = qKeys.join('/');
    // if (isRevalidationPaused(key)) return;
    // const now = Date.now();

    // if (now - (lastRefetchedAt.get(key) || 0) < REFETCH_THROTTLE_MS) return;

    // lastRefetchedAt.set(key, now);
    await queryClient.invalidateQueries({ queryKey: qKeys });
  };

  const filterFetchingQueries = (qKeys: string[][]) =>
    qKeys.filter((qKey) => queryClient.isFetching({ queryKey: qKey }) === 0);

  const scheduleRelatedKeys = [
    queryKeys.workload,
    queryKeys.workloadCalendarSidebar,
    queryKeys.workloadMonthlyCalendar,
    queryKeys.workloadTwoWeeklyCalendar,
    queryKeys.workloadWeeklyCalendar,
    queryKeys.workloadDaily,
    queryKeys.schedules.internal.index,
    queryKeys.schedules.internal.item,
  ];

  const jobRelatedKeys = [queryKeys.jobProject, queryKeys.job, queryKeys.jobStages, queryKeys.jobs];

  const mapQueryType = (type: string) => {
    switch (type) {
      case 'schedule':
        return access.fieldUser
          ? [queryKeys.foremanSchedules, queryKeys.foremanTask, queryKeys.foremanArchive]
          : [queryKeys.hotSeat, ...jobRelatedKeys, ...scheduleRelatedKeys];
      case 'task':
        return access.fieldUser
          ? [queryKeys.foremanTask, queryKeys.foremanStages]
          : [queryKeys.hotSeat, ...jobRelatedKeys, ...scheduleRelatedKeys];
      case 'subtask':
        return access.fieldUser
          ? [queryKeys.foremanTask, queryKeys.foremanStages]
          : [queryKeys.hotSeat, ...jobRelatedKeys, ...scheduleRelatedKeys];
      case 'job':
        return jobRelatedKeys;
      case 'notification':
        return [queryKeys.notifications];
      case 'hot_seat':
        return [queryKeys.hotSeat];
      default:
        return [];
    }
  };

  const revalidate = async (types: string[]) => {
    if (!types.length) return;
    const queries: string[][] = [];
    types.forEach((type) => {
      const tmp = mapQueryType(type);
      tmp.forEach((t) => queries.push(t));
    });

    const promises = filterFetchingQueries(Array.from(new Set(queries))).map((q) => {
      return revalidateQuery(q);
    });

    Promise.allSettled(promises)
      .finally(() => {
        // eslint-disable-next-line no-console
        console.info('queries updated');
      })
      .catch((err) => {
        console.error(err);
      });
  };

  type WindowPlus = Window & {
    revalidateQueries: (types: string[]) => Promise<void>;
  };
  // eslint-disable-next-line immutable/no-mutation
  (window as unknown as WindowPlus).revalidateQueries = revalidate;

  return {
    revalidate,
  };
};
